import { useEffect, useMemo, useState, useRef } from 'react';
import { getShadowRoot } from '@/utils/getShadowRoot';

import {
	getNodeIdxFromClassName,
	getAncestorListingWrapper,
	getAncestorCustomListing,
	isListingSectionPayload,
	isDeniedListingPlacement,
	CUSTOM_LISTING_BLOCK_TYPE,
} from '@bay-email/core';
import { get } from 'lodash';
import { getBlockNodeByChildEle } from '@/utils/getBlockNodeByChildEle';
import { useBlock } from '@/hooks/useBlock';
import { getDirectionPosition } from '@/utils/getDirectionPosition';
import { useFocusIdx } from './useFocusIdx';
import { useDataTransfer } from './useDataTransfer';
import { useHoverIdx } from './useHoverIdx';
import { getInsertPosition } from '@/utils/getInsertPosition';
import { useEditorProps } from './useEditorProps';
import { DATA_ATTRIBUTE_DROP_CONTAINER } from '@/constants';

export function useDropBlock() {
	const [ref, setRef] = useState<HTMLElement | null>(null);
	const { values } = useBlock();
	const { autoComplete } = useEditorProps();
	const { dataTransfer, setDataTransfer } = useDataTransfer();
	const cacheValues = useRef(values);
	const cacheDataTransfer = useRef(dataTransfer);

	useEffect(() => {
		cacheValues.current = values;
	}, [values]);

	useEffect(() => {
		cacheDataTransfer.current = dataTransfer;
	}, [dataTransfer]);
	const { setFocusIdx, focusIdx } = useFocusIdx();
	const { setHoverIdx, setDirection, isDragging, hoverIdx, direction } = useHoverIdx();

	useEffect(() => {
		if (ref) {
			let target: EventTarget | null = null;
			let mouseDownPoint: { x: number; y: number } | null = null;
			const onMouseDown = (ev: MouseEvent) => {
				target = ev.target;
				mouseDownPoint = { x: ev.clientX, y: ev.clientY };
			};

			// Clicking a static text block swaps it for the tiptap editor during the same
			// click, so the event target can already be detached (no parentNode chain to
			// the block). Fall back to whatever is under the pointer now.
			const resolveElement = (el: EventTarget | null, x: number, y: number) => {
				if (el instanceof Element && el.isConnected) return el;
				return getShadowRoot()?.elementFromPoint(x, y) ?? null;
			};

			const onClick = (ev: MouseEvent) => {
				ev.preventDefault(); // prevent link

				const clickEl = resolveElement(ev.target, ev.clientX, ev.clientY);

				// Allow selection if targets are different but both are within the same block
				// This handles contenteditable elements like table cells
				if (target !== ev.target) {
					const mousedownEl = resolveElement(
						target,
						mouseDownPoint?.x ?? ev.clientX,
						mouseDownPoint?.y ?? ev.clientY,
					);
					const mousedownBlock = getBlockNodeByChildEle(mousedownEl);
					const clickBlock = getBlockNodeByChildEle(clickEl);

					// If both targets belong to the same block, continue with selection
					if (!mousedownBlock || !clickBlock || mousedownBlock !== clickBlock) {
						return;
					}
				}

				if (clickEl) {
					const target = getBlockNodeByChildEle(clickEl);
					if (!target) return;
					const idx = getNodeIdxFromClassName(target.classList)!;
					setFocusIdx(idx);
					// scrollBlockEleIntoView({ idx });
				}
			};

			ref.addEventListener('mousedown', onMouseDown);
			ref.addEventListener('click', onClick);
			return () => {
				ref.removeEventListener('mousedown', onMouseDown);
				ref.removeEventListener('click', onClick);
			};
		}
	}, [ref, setFocusIdx]);

	useEffect(() => {
		if (ref) {
			let lastHoverTarget: EventTarget | null = null;

			let lastDragover: {
				target: EventTarget | null;
				valid: boolean;
			} = {
				target: null,
				valid: false,
			};

			const onMouseover = (ev: MouseEvent) => {
				if (lastHoverTarget === ev.target) return;
				lastHoverTarget = ev.target;
				const blockNode = getBlockNodeByChildEle(ev.target as HTMLElement);

				if (blockNode) {
					const idx = getNodeIdxFromClassName(blockNode.classList)!;
					setHoverIdx(idx);
				}
			};

			const onDrop = (ev: MouseEvent) => {
				lastDragover.target = null;
			};

			const onDragOver = (ev: DragEvent) => {
				if (!cacheDataTransfer.current) return;

				const scrollContainer = ref;
				if (scrollContainer) {
					const rect = scrollContainer.getBoundingClientRect();
					const edgeThreshold = 80;
					const maxStep = 18;

					if (ev.clientY < rect.top + edgeThreshold) {
						const distanceToTop = Math.max(ev.clientY - rect.top, 0);
						const step = Math.max(
							2,
							Math.round(((edgeThreshold - distanceToTop) / edgeThreshold) * maxStep),
						);
						scrollContainer.scrollTop -= step;
					} else if (ev.clientY > rect.bottom - edgeThreshold) {
						const distanceToBottom = Math.max(rect.bottom - ev.clientY, 0);
						const step = Math.max(
							2,
							Math.round(((edgeThreshold - distanceToBottom) / edgeThreshold) * maxStep),
						);
						scrollContainer.scrollTop += step;
					}
				}

				// if (ev.target === lastDragover.target) {
				//   if (lastDragover.valid) {
				//     ev.preventDefault();

				//     return;
				//   }
				// }

				lastDragover.target = ev.target;
				lastDragover.valid = false;

				const blockNode = getBlockNodeByChildEle(ev.target as HTMLDivElement);

				if (blockNode) {
					const directionPosition = getDirectionPosition(ev);
					const idx = getNodeIdxFromClassName(blockNode.classList)!;
					const positionData = getInsertPosition({
						context: cacheValues.current,
						idx,
						directionPosition,
						dragType: cacheDataTransfer.current.type,
					});

					// A Listing section sets a listing per column, so it can't live in a
					// Listing Wrapper (whose listing already cascades to everything).
					// Decline the target so the drop indicator never appears, instead
					// of accepting the drag and refusing it on release.
					const targetBlock = positionData
						? (get(cacheValues.current, positionData.parentIdx) as
								| { type?: string }
								| undefined)
						: undefined;
					const listingSectionInWrapper =
						isListingSectionPayload(cacheDataTransfer.current.payload) &&
						(targetBlock?.type === CUSTOM_LISTING_BLOCK_TYPE ||
							Boolean(
								positionData &&
									getAncestorListingWrapper(
										(cacheValues.current as any)?.content,
										positionData.parentIdx,
									),
							));

					// Same idea for the listing blocks themselves: a Listing Wrapper
						// or a listing column already supplies the listing, so refuse the
						// target here too rather than only on release.
						const listingBlockInScope =
							positionData &&
							isDeniedListingPlacement(
								cacheDataTransfer.current.type,
								targetBlock as any,
								Boolean(
									getAncestorCustomListing(
										(cacheValues.current as any)?.content,
										positionData.parentIdx,
									),
								),
							);

						if (positionData && !listingSectionInWrapper && !listingBlockInScope) {
						ev.preventDefault();
						lastDragover.valid = true;
						setDataTransfer((dataTransfer: any) => {
							return {
								...dataTransfer,
								parentIdx: positionData.parentIdx,
								positionIndex: positionData.insertIndex,
								endDirection: positionData.endDirection,
							};
						});
						setDirection(positionData.endDirection);
						setHoverIdx(positionData.hoverIdx);
					}
				}
				if (!lastDragover.valid) {
					setDirection('');
					setHoverIdx('');
					setDataTransfer((dataTransfer: any) => {
						return {
							...dataTransfer,
							parentIdx: undefined,
						};
					});
				}
			};

			const onCheckDragLeave = (ev: DragEvent) => {
				const dropEleList = [
					...document.querySelectorAll(`[${DATA_ATTRIBUTE_DROP_CONTAINER}="true"]`),
				];
				const target = ev.target as HTMLElement;
				const isDropContainer = dropEleList.some(ele => ele.contains(target));

				if (!isDropContainer) {
					setDirection('');
					setHoverIdx('');
					setDataTransfer((dataTransfer: any) => {
						return {
							...dataTransfer,
							parentIdx: undefined,
						};
					});
				}
			};

			ref.addEventListener('mouseover', onMouseover);
			// ref.addEventListener('mouseout', onMouseOut);
			ref.addEventListener('drop', onDrop);
			ref.addEventListener('dragover', onDragOver);
			window.addEventListener('dragover', onCheckDragLeave);

			return () => {
				ref.removeEventListener('mouseover', onMouseover);
				// ref.removeEventListener('mouseout', onMouseOut);
				ref.removeEventListener('drop', onDrop);
				ref.removeEventListener('dragover', onDragOver);
				window.removeEventListener('dragover', onCheckDragLeave);
			};
		}
	}, [autoComplete, cacheDataTransfer, ref, setDataTransfer, setDirection, setHoverIdx]);

	useEffect(() => {
		if (!ref) return;

		const onMouseOut = (ev: MouseEvent) => {
			if (!isDragging) {
				ev.stopPropagation();
				setHoverIdx('');
			}
		};
		ref.addEventListener('mouseout', onMouseOut);
		return () => {
			ref.removeEventListener('mouseout', onMouseOut);
		};
	}, [isDragging, ref, setHoverIdx]);

	useEffect(() => {
		if (ref) {
			ref.setAttribute('data-dragging', String(isDragging));
			ref.setAttribute('data-direction', direction || 'none');
		}
	}, [direction, isDragging, ref]);

	useEffect(() => {
		if (ref) {
			ref.setAttribute('data-hoverIdx', hoverIdx);
		}
	}, [hoverIdx, ref]);

	useEffect(() => {
		if (ref) {
			ref.setAttribute('data-focusIdx', focusIdx);
		}
	}, [focusIdx, ref]);

	return useMemo(
		() => ({
			setRef,
		}),
		[setRef],
	);
}
